import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { VerificationService, VerificationStatus } from 'app/services/http/verification.service';
import { loadSettingsFirstSteps } from 'app/state/account/account.actions';
import { interval, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

type VerificationChannel = 'email' | 'phone';

@Component({
  selector: 'app-first-access-email-verification-step',
  templateUrl: './email-verification-step.component.html',
  styleUrls: ['./email-verification-step.component.scss']
})
export class EmailVerificationStepComponent implements OnInit, OnDestroy {
  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() verifiedChange = new EventEmitter<boolean>();
  /** Emite true quando o envio de código por e-mail está indisponível na API (ex.: ambiente sem provedor configurado ou instabilidade). */
  @Output() unavailableChange = new EventEmitter<boolean>();

  verificationStatus: VerificationStatus | null = null;
  isLoading = false;
  emailSending = false;
  emailVerifying = false;
  emailCode = '';
  emailCodeError = '';
  emailCodeSent = false;
  emailCooldown = 0;
  sentEmail = '';
  phoneSending = false;
  phoneVerifying = false;
  phoneCode = '';
  phoneCodeError = '';
  phoneCodeSent = false;
  phoneCooldown = 0;
  sentPhone = '';
  emailChannelUnavailable = false;
  phoneChannelUnavailable = false;

  private emailCooldownSubscription?: Subscription;
  private phoneCooldownSubscription?: Subscription;
  private initialCodesDispatched = false;

  constructor(
    private verificationService: VerificationService,
    private alertService: AlertService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.loadVerificationStatus();
  }

  ngOnDestroy(): void {
    this.emailCooldownSubscription?.unsubscribe();
    this.phoneCooldownSubscription?.unsubscribe();
    this.loadingChange.emit(false);
  }

  get emailFieldValue(): string {
    if (!this.verificationStatus?.email_verified) {
      return this.emailCode;
    }

    return this.emailCode || 'Validado';
  }

  get phoneFieldValue(): string {
    if (!this.verificationStatus?.phone_verified) {
      return this.phoneCode;
    }

    return this.phoneCode || 'Validado';
  }

  get hasPhoneNumber(): boolean {
    return !!this.verificationStatus?.phone_number;
  }

  sendEmailVerification(options: { silent?: boolean } = {}): void {
    const silent = !!options.silent;

    if (!this.verificationStatus || this.emailSending || this.emailCooldown > 0) {
      return;
    }

    if (this.verificationStatus.email_verified) {
      if (!silent) {
        this.alertService.success('Email já verificado.');
      }
      return;
    }

    if (!this.verificationStatus.email) {
      return;
    }

    this.emailSending = true;
    this.emitBusyState();

    this.verificationService.sendEmailVerification(this.verificationStatus.email)
      .pipe(finalize(() => {
        this.emailSending = false;
        this.emitBusyState();
      }))
      .subscribe({
        next: (response) => {
          this.sentEmail = this.verificationStatus!.email;
          this.emailCodeSent = true;
          if (!silent) {
            this.alertService.success(response.message || 'Código de verificação enviado para seu email.');
          }
          this.startEmailCooldown(response.data?.cooldown_remaining || 60);
        },
        error: (error) => {
          if (this.isChannelUnavailableError(error)) {
            this.setEmailChannelUnavailable();
            return;
          }

          // O HttpService repassa o corpo do erro: o cooldown vem em `error.data`.
          const cooldown = error?.data?.cooldown_remaining ?? error?.error?.data?.cooldown_remaining;

          if (cooldown) {
            this.sentEmail = this.verificationStatus!.email;
            this.emailCodeSent = true;
            this.startEmailCooldown(cooldown);
          }

          if (!silent || !cooldown) {
            this.alertService.error(error);
          }
        }
      });
  }

  verifyEmailCode(): void {
    if (!this.verificationStatus || this.emailCode.length !== 6) {
      this.alertService.error('Informe o código de 6 dígitos enviado por email.');
      return;
    }

    this.emailVerifying = true;
    this.emailCodeError = '';
    this.emitBusyState();

    this.verificationService.verifyEmail(this.verificationStatus.email, this.emailCode)
      .pipe(finalize(() => {
        this.emailVerifying = false;
        this.emitBusyState();
      }))
      .subscribe({
        next: (response) => {
          this.emailCooldown = 0;
          this.emailCooldownSubscription?.unsubscribe();
          this.emailCodeError = '';
          this.alertService.success(response.message || 'Email verificado com sucesso.');
          this.store.dispatch(loadSettingsFirstSteps());
          this.loadVerificationStatus();
        },
        error: (error) => {
          this.emailCodeError = 'Código incorreto. Confira e tente novamente.';
          this.alertService.error(error);
        }
      });
  }

  sendSmsVerification(options: { silent?: boolean } = {}): void {
    const silent = !!options.silent;

    if (!this.verificationStatus || this.phoneSending || this.phoneCooldown > 0) {
      return;
    }

    if (this.verificationStatus.phone_verified) {
      if (!silent) {
        this.alertService.success('Telefone já verificado.');
      }
      return;
    }

    if (!this.verificationStatus.phone_number) {
      if (!silent) {
        this.alertService.error('Cadastre um telefone antes de enviar o SMS de verificação.');
      }
      return;
    }

    this.phoneSending = true;
    this.emitBusyState();

    this.verificationService.sendSmsVerification(this.verificationStatus.phone_number)
      .pipe(finalize(() => {
        this.phoneSending = false;
        this.emitBusyState();
      }))
      .subscribe({
        next: (response) => {
          this.sentPhone = this.verificationStatus!.phone_number;
          this.phoneCodeSent = true;
          if (!silent) {
            this.alertService.success(response.message || 'Código de verificação enviado via SMS.');
          }
          this.startPhoneCooldown(response.data?.cooldown_remaining || 60);
        },
        error: (error) => {
          if (this.isChannelUnavailableError(error)) {
            this.phoneChannelUnavailable = true;
            return;
          }

          // O HttpService repassa o corpo do erro: o cooldown vem em `error.data`.
          const cooldown = error?.data?.cooldown_remaining ?? error?.error?.data?.cooldown_remaining;

          if (cooldown) {
            this.sentPhone = this.verificationStatus!.phone_number;
            this.phoneCodeSent = true;
            this.startPhoneCooldown(cooldown);
          }

          if (!silent || !cooldown) {
            this.alertService.error(error);
          }
        }
      });
  }

  verifyPhoneCode(): void {
    if (!this.verificationStatus || this.phoneCode.length !== 6) {
      this.alertService.error('Informe o código de 6 dígitos enviado por SMS.');
      return;
    }

    if (!this.verificationStatus.phone_number) {
      this.alertService.error('Cadastre um telefone antes de validar o código por SMS.');
      return;
    }

    this.phoneVerifying = true;
    this.phoneCodeError = '';
    this.emitBusyState();

    this.verificationService.verifyPhone(this.verificationStatus.phone_number, this.phoneCode)
      .pipe(finalize(() => {
        this.phoneVerifying = false;
        this.emitBusyState();
      }))
      .subscribe({
        next: (response) => {
          this.phoneCooldown = 0;
          this.phoneCooldownSubscription?.unsubscribe();
          this.phoneCodeError = '';
          this.alertService.success(response.message || 'Telefone verificado com sucesso.');
          this.store.dispatch(loadSettingsFirstSteps());
          this.loadVerificationStatus();
        },
        error: (error) => {
          this.phoneCodeError = 'Código incorreto. Confira e tente novamente.';
          this.alertService.error(error);
        }
      });
  }

  onCodeInput(event: Event, channel: VerificationChannel): void {
    const input = event.target as HTMLInputElement;
    const code = (input.value || '').replace(/\D/g, '').slice(0, 6);

    if (input.value !== code) {
      input.value = code;
    }

    if (channel === 'email') {
      this.emailCode = code;
      this.emailCodeError = '';

      if (code.length === 6 && !this.emailVerifying && !this.verificationStatus?.email_verified) {
        this.verifyEmailCode();
      }

      return;
    }

    this.phoneCode = code;
    this.phoneCodeError = '';

    if (code.length === 6 && !this.phoneVerifying && !this.verificationStatus?.phone_verified) {
      this.verifyPhoneCode();
    }
  }

  private loadVerificationStatus(): void {
    this.setLoading(true);

    this.verificationService.getStatus()
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe({
        next: (status) => {
          this.verificationStatus = status;
          this.verifiedChange.emit(status.email_verified);

          if (status.email_verified) {
            this.emailCodeError = '';
            this.sentEmail = '';
          }

          if (status.phone_verified) {
            this.phoneCodeError = '';
            this.sentPhone = '';
          }

          this.dispatchInitialCodes(status);
        },
        error: (error) => {
          this.verifiedChange.emit(false);
          this.alertService.error(error);
        }
      });
  }

  private dispatchInitialCodes(status: VerificationStatus): void {
    if (this.initialCodesDispatched) {
      return;
    }

    this.initialCodesDispatched = true;

    if (!status.email_verified && status.email) {
      this.sendEmailVerification({ silent: true });
    }

    if (!status.phone_verified && status.phone_number) {
      this.sendSmsVerification({ silent: true });
    }
  }

  /**
   * A API responde 400 com error_code CHANNEL_UNAVAILABLE quando o canal (e-mail/SMS)
   * não pode enviar códigos. Nesse caso o primeiro acesso não deve travar o cliente.
   */
  private isChannelUnavailableError(error: any): boolean {
    // O HttpService repassa o corpo da resposta (com `status` trocado pelo código HTTP);
    // `error.error` cobre o caso de receber o HttpErrorResponse cru.
    const errorCode = error?.error_code ?? error?.error?.error_code;

    return errorCode === 'CHANNEL_UNAVAILABLE';
  }

  private setEmailChannelUnavailable(): void {
    this.emailChannelUnavailable = true;
    this.emailCodeSent = false;
    this.unavailableChange.emit(true);
  }

  private startEmailCooldown(seconds: number): void {
    this.emailCooldownSubscription?.unsubscribe();
    this.emailCooldown = seconds;

    this.emailCooldownSubscription = interval(1000)
      .pipe(take(seconds))
      .subscribe(() => {
        this.emailCooldown--;
      });
  }

  private startPhoneCooldown(seconds: number): void {
    this.phoneCooldownSubscription?.unsubscribe();
    this.phoneCooldown = seconds;

    this.phoneCooldownSubscription = interval(1000)
      .pipe(take(seconds))
      .subscribe(() => {
        this.phoneCooldown--;
      });
  }

  private setLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
    this.emitBusyState();
  }

  private emitBusyState(): void {
    this.loadingChange.emit(
      this.isLoading
      || this.emailSending
      || this.emailVerifying
      || this.phoneSending
      || this.phoneVerifying
    );
  }
}
